import { __decorate } from 'tslib';
import { DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import * as i0 from '@angular/core';
import { EventEmitter, ElementRef, Component, ViewEncapsulation, Input, Output, ViewChild, NgModule } from '@angular/core';
import * as i4 from '@rogers/cdk/button';
import { ButtonComponent, ButtonModule } from '@rogers/cdk/button';
import { Required, Default } from '@rogers/cdk/common/decorators';
import { Subject, Subscription, fromEvent } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import tippy from 'tippy.js';
import * as i1 from '@rogers/cdk/common/services';
import * as i2 from '@rogers/cdk/modal';
import { ModalModule } from '@rogers/cdk/modal';
import * as i3 from '@rogers/cdk/icon';
import { IconModule } from '@rogers/cdk/icon';
import * as i5 from '@angular/cdk/a11y';
import { A11yModule } from '@angular/cdk/a11y';
import * as i6 from '@angular/common';
import { CommonModule } from '@angular/common';

var NodeType;
(function (NodeType) {
    NodeType["DSPOPOVER"] = "DS-POPOVER";
    NodeType["LABEL"] = "LABEL";
    NodeType["SPAN"] = "SPAN";
    NodeType["P"] = "P";
})(NodeType || (NodeType = {}));
class PopoverComponent {
    constructor(el, _renderer, windowService, vcr, componentFactoryResolver, appRef, injector, _cdr, utilsService, modalService) {
        this.el = el;
        this._renderer = _renderer;
        this.windowService = windowService;
        this.vcr = vcr;
        this.componentFactoryResolver = componentFactoryResolver;
        this.appRef = appRef;
        this.injector = injector;
        this._cdr = _cdr;
        this.utilsService = utilsService;
        this.modalService = modalService;
        this.destroyed$ = new Subject();
        this.end$ = new Subject();
        this._isShown = false;
        this.documentKeydownEventSub = Subscription.EMPTY;
        this.isAppleMobileDevice = false;
        this.elementClick = new EventEmitter();
        this.utilsService
            .getApplicationRoot()
            .pipe(first())
            .pipe(takeUntil(this.destroyed$))
            .subscribe((root) => {
            this.appRoot = root;
        });
    }
    get isInline() {
        return this.el?.nativeElement?.nodeName !== NodeType.DSPOPOVER;
    }
    get mode() {
        return this._mode;
    }
    set mode(value) {
        this._mode = value;
        this.setMode();
    }
    get content() {
        return this._content;
    }
    set content(content) {
        if (content instanceof HTMLElement || typeof content === 'string') {
            this._content = content;
            return;
        }
        throw new Error(`Type is not matching with any of the allowed types: 'string' | ElementRef<HTMLElement>: ${content}`);
    }
    ngOnInit() { }
    ngAfterViewInit() {
        this._createPopover();
    }
    ngAfterContentInit() {
        this.isAppleMobileDevice = this.windowService.isAppleMobileDevice()
            ? true
            : false;
        this._cdr.markForCheck();
    }
    ngOnChanges(changes) {
        if (this._tippy) {
            if (changes.content && !changes.content.isFirstChange()) {
                this._tippy.popperChildren.content.innerHTML = '';
                this.addTippyContent(this._tippy.popperChildren.content);
            }
        }
    }
    setMode() {
        if (this._tippy) {
            if (this.mode) {
                this._tippy.popper.setAttribute('mode', this.mode);
            }
            else {
                this._tippy.popper.removeAttribute('mode');
            }
        }
    }
    getContent() {
        if (this.content instanceof HTMLElement) {
            return this.content;
        }
        const el = this._renderer.createElement('div');
        el.innerHTML = this.content;
        return el;
    }
    _createPopover() {
        if (!this.windowService.isBrowser()) {
            return;
        }
        if (this._tippy) {
            this._tippy.show();
            return;
        }
        this._tippy = tippy(this.infoIconRef.nativeElement, {
            content: '',
            arrow: true,
            theme: 'ds',
            placement: this.placement || 'auto',
            trigger: 'manual',
            interactive: true,
            showOnInit: false,
            zIndex: this.zIndex || 2,
            onShow: this.onPopoverShow.bind(this),
            onHide: this.onPopoverHide.bind(this),
        });
        this.setMode();
        this.addTippyContent(this._tippy.popperChildren.content);
    }
    onPopoverHide() {
        this.documentKeydownEventSub.unsubscribe();
        if (this.windowService.isBrowser()) {
            if (this.appRoot && this.modalService.openModals.length === 0) {
                this.appRoot.location.nativeElement.setAttribute('aria-hidden', 'false');
            }
        }
    }
    onPopoverShow(t) {
        Promise.resolve().then(() => {
            this.focusElement(this.tippyContentFirstTabElement);
            if (this.windowService.isBrowser()) {
                if (this.appRoot) {
                    this.appRoot.location.nativeElement.setAttribute('aria-hidden', 'true');
                }
            }
        });
        if (!this._isShown) {
            // Set attributes properties to handle voice over
            t.popperChildren.tooltip.tabIndex = -1;
            // Add hollow arrow to match the design requirement
            this.addHollowArrow(t.popperChildren.arrow);
            // Add close button once the tooltip object is created
            // Set this flag to true so dynamic elements don't get injected multiple times
            this._isShown = true;
        }
    }
    addHollowArrow(target) {
        const hollowArrow = this._renderer.createElement('div');
        hollowArrow.className = 'tippy-arrow-hollow';
        target.appendChild(hollowArrow);
    }
    addTippyContent(target) {
        Promise.resolve().then(() => {
            this.tippyContentDomPortalHost = new DomPortalOutlet(target, this.componentFactoryResolver, this.appRef, this.injector);
            this.tippyContentPortal = new TemplatePortal(this.tippyContentRef, this.vcr);
            this.tippyContentDomPortalHost.attach(this.tippyContentPortal);
            this.attachForeignContent(this.tippyContentDomPortalHost);
        });
    }
    attachForeignContent(host) {
        const popoverContentInjectionArea = host.outletElement.querySelector('[popover-content-injection-area]');
        popoverContentInjectionArea.innerHTML = '';
        popoverContentInjectionArea.appendChild(this.getContent());
        this.tippyContentFirstTabElement = popoverContentInjectionArea;
    }
    stopEventPropagation(event) {
        event.preventDefault();
        event.stopPropagation();
    }
    showPopover(event) {
        const e = event;
        if (event.type === 'click' ||
            e.key === 'Enter' ||
            e.code === 'Enter') {
            this.stopEventPropagation(event);
            this._createPopover();
        }
        if (this.windowService.isBrowser()) {
            this.documentKeydownEventSub = fromEvent(document, 'keydown')
                .pipe(takeUntil(this.end$))
                .subscribe((ev) => {
                if (ev.key === 'Escape' || ev.code === 'Escape') {
                    this._hidePopover(ev);
                    this.end$.next(void 0);
                    this.end$.complete();
                }
            });
        }
        this.elementClick.emit(this);
    }
    _hidePopover(event) {
        const e = event;
        if (event.type === 'click' ||
            e.key === 'Enter' ||
            e.code === 'Enter' ||
            e.key === 'Escape' ||
            e.code === 'Escape' ||
            e.key === 'Spacebar' ||
            e.code === 'Space') {
            this.stopEventPropagation(event);
            this.closePopover();
        }
        this.focusElement(this.popoverTrigger.nativeElement);
    }
    closePopover() {
        this._tippy.hide();
    }
    focusElement(target) {
        const x = window.scrollX;
        const y = window.scrollY;
        if (this.windowService.isMobile()) {
            setTimeout(() => {
                target.focus();
            }, 0);
        }
        if (this.windowService.isDesktop()) {
            target.focus();
            window.scrollTo(x, y);
        }
    }
    regionFocus(e, target) {
        if (target instanceof ButtonComponent) {
            this.focusElement(target.nativeElement);
            return;
        }
        this.focusElement(target);
    }
    ngOnDestroy() {
        this.destroyed$.next(void 0);
        this.destroyed$.complete();
        this.end$.next(void 0);
        this.end$.complete();
        if (this._tippy) {
            this._tippy.destroy();
        }
        this.onPopoverHide();
    }
}
PopoverComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PopoverComponent, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i1.WindowService }, { token: i0.ViewContainerRef }, { token: i0.ComponentFactoryResolver }, { token: i0.ApplicationRef }, { token: i0.Injector }, { token: i0.ChangeDetectorRef }, { token: i1.UtilsService }, { token: i2.ModalService }], target: i0.ɵɵFactoryTarget.Component });
PopoverComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: PopoverComponent, selector: "ds-popover, span[dsPopover], p[dsPopover], label[dsPopover]", inputs: { mode: "mode", content: "content", triggerLabel: "triggerLabel", triggerCaption: "triggerCaption", closeLabel: "closeLabel", closeCaption: "closeCaption", placement: "placement", zIndex: "zIndex", reverse: "reverse" }, outputs: { elementClick: "elementClick" }, host: { properties: { "class.reverse": "reverse" } }, viewQueries: [{ propertyName: "popoverTrigger", first: true, predicate: ["popoverTrigger"], descendants: true }, { propertyName: "infoIconRef", first: true, predicate: ["infoIconRef"], descendants: true, read: ElementRef }, { propertyName: "tippyContentRef", first: true, predicate: ["tippyContentRef"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<button\n  #popoverTrigger\n  [attr.title]=\"triggerCaption || ''\"\n  [attr.aria-label]=\"triggerLabel\"\n  class=\"ds-popover outline-none ds-pointer ds-bgcolor-transparent b-0\"\n  [ngClass]=\"{ '-inline': isInline }\"\n  (click)=\"showPopover($event)\"\n  (keypress)=\"showPopover($event)\"\n  type=\"button\"\n  cdkMonitorSubtreeFocus\n>\n  <ng-content></ng-content>\n  <ds-icon\n    #infoIconRef\n    tabindex=\"-1\"\n    class=\"ds-popover__icon outline-none\"\n    name=\"info\"\n  ></ds-icon>\n</button>\n\n<ng-template #tippyContentRef>\n  <div\n    tabindex=\"0\"\n    class=\"cdk-visually-hidden\"\n    aria-hidden=\"true\"\n    (focus)=\"regionFocus($event, tippyContentLastTabElement)\"\n  ></div>\n  <div\n    class=\"ds-popover__body outline-none\"\n    [attr.popover-content-injection-area]=\"true\"\n    #tippyContentFirstTabElement\n    tabindex=\"0\"\n    cdkMonitorElementFocus\n    [attr.role]=\"isAppleMobileDevice ? 'alertdialog' : 'dialog'\"\n  ></div>\n  <button\n    ds-button\n    [title]=\"closeCaption || ''\"\n    [variant]=\"'icon'\"\n    (click)=\"_hidePopover($event)\"\n    (keypress)=\"_hidePopover($event)\"\n    class=\"-close ds-popover__close\"\n    [attr.close-button]=\"true\"\n    #tippyContentLastTabElement\n  >\n    {{ closeLabel }}\n    <ds-icon name=\"close\"></ds-icon>\n  </button>\n  <div\n    tabindex=\"0\"\n    class=\"cdk-visually-hidden\"\n    aria-hidden=\"true\"\n    (focus)=\"regionFocus($event, tippyContentFirstTabElement)\"\n  ></div>\n</ng-template>\n", components: [{ type: i3.IconComponent, selector: "ds-icon", inputs: ["name", "color", "caption", "ariaHidden", "size"] }, { type: i4.ButtonComponent, selector: "button[ds-button], a[ds-button]", inputs: ["variant", "size", "alignIcon", "img", "transparent", "disabled", "tabIndex", "a11yDescription"] }], directives: [{ type: i5.CdkMonitorFocus, selector: "[cdkMonitorElementFocus], [cdkMonitorSubtreeFocus]", outputs: ["cdkFocusChange"] }, { type: i6.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], encapsulation: i0.ViewEncapsulation.None });
__decorate([
    Required()
], PopoverComponent.prototype, "content", null);
__decorate([
    Required()
], PopoverComponent.prototype, "triggerLabel", void 0);
__decorate([
    Required()
], PopoverComponent.prototype, "closeLabel", void 0);
__decorate([
    Default('')
], PopoverComponent.prototype, "closeCaption", void 0);
__decorate([
    Default(false)
], PopoverComponent.prototype, "reverse", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PopoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ds-popover, span[dsPopover], p[dsPopover], label[dsPopover]', encapsulation: ViewEncapsulation.None, host: {
                        '[class.reverse]': 'reverse',
                    }, template: "<button\n  #popoverTrigger\n  [attr.title]=\"triggerCaption || ''\"\n  [attr.aria-label]=\"triggerLabel\"\n  class=\"ds-popover outline-none ds-pointer ds-bgcolor-transparent b-0\"\n  [ngClass]=\"{ '-inline': isInline }\"\n  (click)=\"showPopover($event)\"\n  (keypress)=\"showPopover($event)\"\n  type=\"button\"\n  cdkMonitorSubtreeFocus\n>\n  <ng-content></ng-content>\n  <ds-icon\n    #infoIconRef\n    tabindex=\"-1\"\n    class=\"ds-popover__icon outline-none\"\n    name=\"info\"\n  ></ds-icon>\n</button>\n\n<ng-template #tippyContentRef>\n  <div\n    tabindex=\"0\"\n    class=\"cdk-visually-hidden\"\n    aria-hidden=\"true\"\n    (focus)=\"regionFocus($event, tippyContentLastTabElement)\"\n  ></div>\n  <div\n    class=\"ds-popover__body outline-none\"\n    [attr.popover-content-injection-area]=\"true\"\n    #tippyContentFirstTabElement\n    tabindex=\"0\"\n    cdkMonitorElementFocus\n    [attr.role]=\"isAppleMobileDevice ? 'alertdialog' : 'dialog'\"\n  ></div>\n  <button\n    ds-button\n    [title]=\"closeCaption || ''\"\n    [variant]=\"'icon'\"\n    (click)=\"_hidePopover($event)\"\n    (keypress)=\"_hidePopover($event)\"\n    class=\"-close ds-popover__close\"\n    [attr.close-button]=\"true\"\n    #tippyContentLastTabElement\n  >\n    {{ closeLabel }}\n    <ds-icon name=\"close\"></ds-icon>\n  </button>\n  <div\n    tabindex=\"0\"\n    class=\"cdk-visually-hidden\"\n    aria-hidden=\"true\"\n    (focus)=\"regionFocus($event, tippyContentFirstTabElement)\"\n  ></div>\n</ng-template>\n" }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i1.WindowService }, { type: i0.ViewContainerRef }, { type: i0.ComponentFactoryResolver }, { type: i0.ApplicationRef }, { type: i0.Injector }, { type: i0.ChangeDetectorRef }, { type: i1.UtilsService }, { type: i2.ModalService }]; }, propDecorators: { mode: [{
                type: Input
            }], content: [{
                type: Input
            }], triggerLabel: [{
                type: Input
            }], triggerCaption: [{
                type: Input
            }], closeLabel: [{
                type: Input
            }], closeCaption: [{
                type: Input
            }], placement: [{
                type: Input
            }], zIndex: [{
                type: Input
            }], reverse: [{
                type: Input
            }], elementClick: [{
                type: Output
            }], popoverTrigger: [{
                type: ViewChild,
                args: ['popoverTrigger', { static: false }]
            }], infoIconRef: [{
                type: ViewChild,
                args: ['infoIconRef', { static: false, read: ElementRef }]
            }], tippyContentRef: [{
                type: ViewChild,
                args: ['tippyContentRef', { static: false }]
            }] } });

class PopoverModule {
}
PopoverModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PopoverModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PopoverModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PopoverModule, declarations: [PopoverComponent], imports: [CommonModule, IconModule, ButtonModule, A11yModule, ModalModule], exports: [PopoverComponent] });
PopoverModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PopoverModule, imports: [[CommonModule, IconModule, ButtonModule, A11yModule, ModalModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: PopoverModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [PopoverComponent],
                    imports: [CommonModule, IconModule, ButtonModule, A11yModule, ModalModule],
                    exports: [PopoverComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { PopoverComponent, PopoverModule };
